import { Field, FinalFormSelect } from "@comet/admin";
import { type VividFieldProps } from "@src/components/fields/interfaces";
import { PrimitiveAutocomplete } from "@src/components/fields/PrimitiveAutocomplete";
import { type FC, type PropsWithChildren } from "react";
import styled from "styled-components";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = { label: string; value: any };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOptionObject = (option: any): option is Option => option != null && typeof option === "object";

// FinalFormSelect renders a clear ("X") adornment on every non-required select and hard-codes its own
// `endAdornment`, so it can't be suppressed via props. This CSS hides it on the (non-searchable)
// FinalFormSelect fallback; the searchable FinalFormAutocomplete path uses its own `clearable` prop.
const SelectFieldWrapper = styled.div<{ $clearable: boolean }>`
    ${({ $clearable }) =>
        $clearable
            ? ""
            : `
        .CometAdminClearInputAdornment-root {
            display: none;
        }
    `}
`;

/**
 * Form/filter select over a fixed set of options.
 *
 * Defaults to a searchable (type-to-filter) autocomplete. Set `searchable={false}` for selects whose
 * option values are falsy (booleans, or a numeric range that includes 0): Comet's Autocomplete coerces
 * falsy values to "nothing selected", so those must keep using the plain FinalFormSelect dropdown.
 */
export const StaticSelectField: FC<PropsWithChildren<VividFieldProps & { searchable?: boolean }>> = ({
    required = false,
    searchable = true,
    component,
    variant,
    label,
    placeholder,
    options,
    // translate / strip react-select-only props (would otherwise leak to DOM under MUI Select)

    isClearable,
    isDisabled,
    ...rest
}) => {
    const effectiveVariant = variant ?? (label ? "horizontal" : "vertical");

    // Both FinalFormSelect and Comet's Autocomplete store whatever they find in `options` as the field
    // value. When passed `{ label, value }` objects they therefore store the whole object, and callers
    // that send the value straight into a GraphQL variable (every filter/form here does) end up sending
    // an object where a scalar/enum is expected — the request fails with a 400. To keep the field value
    // the primitive `value` (the app-wide contract), feed the bare values and map value -> label for
    // display, mirroring CountrySelect. Primitive option arrays (e.g. from CountrySelect) pass through.
    const optionList: unknown[] = Array.isArray(options) ? options : [];
    const objectOptions = optionList.filter(isOptionObject);
    const hasObjectOptions = objectOptions.length > 0;
    const selectOptions = hasObjectOptions ? optionList.map((option) => (isOptionObject(option) ? option.value : option)) : options;
    const labelByValue = hasObjectOptions ? new Map(objectOptions.map((option) => [option.value, option.label])) : undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getOptionLabel = (value: any): string => {
        if (value === undefined || value === null || value === "") return placeholder ?? "";
        if (labelByValue?.has(value)) return String(labelByValue.get(value));
        return value?.label ?? String(value);
    };

    if (searchable) {
        return (
            <Field
                component={component ?? PrimitiveAutocomplete}
                fieldContainerProps={{ variant: effectiveVariant }}
                fullWidth
                required={required}
                disabled={isDisabled ?? rest.disabled}
                label={label}
                clearable={Boolean(isClearable)}
                placeholder={placeholder}
                options={selectOptions}
                getOptionLabel={getOptionLabel}
                {...rest}
            />
        );
    }

    // FinalFormSelect has no `placeholder` prop. MUI Select hides its rendered value when empty, so set
    // `displayEmpty` and have `getOptionLabel` return the placeholder text for the empty value — that
    // string is what MUI renders into the empty Select.
    return (
        <SelectFieldWrapper $clearable={Boolean(isClearable)}>
            <Field
                component={component ?? FinalFormSelect}
                fieldContainerProps={{ variant: effectiveVariant }}
                fullWidth
                required={required}
                disabled={isDisabled ?? rest.disabled}
                label={label}
                options={selectOptions}
                displayEmpty={placeholder != null}
                getOptionLabel={getOptionLabel}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getOptionValue={(value: any) => (value === undefined || value === null ? "" : String(value))}
                {...rest}
            />
        </SelectFieldWrapper>
    );
};
